import {
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  OnInit,
  HostListener,
  inject,
  signal,
  computed
} from '@angular/core';
import {
  Router,
  ActivatedRoute,
  RouterOutlet,
  NavigationEnd,
  RouterLink,
  RouterLinkActive
} from '@angular/router';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {filter, takeUntil, Subject} from 'rxjs';

import {TopActionsComponent, TopAction} from '../top-actions/top-actions.component';
import {MainSelectorComponent} from '../../shared/selectors/main-selector.component';
import {SubSelectorComponent} from '../../shared/selectors/sub-selector.component';
import {
  MenuService,
  MainOption,
  SubOption,
  Section,
  SubSection
} from '../../core/menu.service';
import {AuthService, MeProfile} from '../../core/auth/auth.service';
import {MAIN_ROUTE} from '../../app.routes';
import { UserProfileService, UserProfile } from '../../core/user-profile/user-profile.service';
import { MenuApiService, MobileMenuItem } from '../../core/menu-api.service';
import {NotificationCenterService} from '../../features/notificaciones/notification-center.service';

const ASSET = (n: string) => `/assets/${n}.svg`;
const EXCLUSIVE_REAL_ESTATE_TYPE = 'exclusive-real-estate';
const EXCLUSIVE_REAL_ESTATE_ROUTE = '/real-estate/real-estate/quotes';

@Component({
  standalone: true,
  selector: 'app-home',
  imports: [
    CommonModule,
    FormsModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MainSelectorComponent,
    SubSelectorComponent,
    TopActionsComponent,
    NgOptimizedImage
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush     // ← NUEVO
})
export class HomeComponent implements OnDestroy, OnInit {
  private readonly notificationCenter = inject(NotificationCenterService);

  /**
   * Closes any open section dropdown when the user clicks outside.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // If there is a dropdown open, close it
    if (this.openedSectionRoute() !== null) {
      this.openedSectionRoute.set(null);
    }
  }


  readonly activeMain = signal<MainOption | undefined>(undefined);
  readonly activeSub = signal<SubOption | undefined>(undefined);
  readonly activeSection = signal<Section | undefined>(undefined);

  readonly subOptions = signal<SubOption[]>([]);
  readonly visibleSubOptions = signal<SubOption[]>([]);
  readonly sections = signal<Section[]>([]);

  readonly showUserMenu = signal(false);
  readonly showProfileSwitchMenu = signal(false);
  readonly myProfiles = signal<MeProfile[]>([]);
  readonly isLoadingProfiles = signal(false);
  readonly switchingProfileUuid = signal<string | null>(null);
  readonly selectedSubSection = signal<SubSection | undefined>(undefined);
  readonly selectedSubSectionParentRoute = signal<string | undefined>(undefined);
  readonly userProfile = signal<UserProfile | null>(null);
  readonly topBarRoleName = computed(() => {
    const roleName = this.userProfile()?.roleName?.trim();
    if (!roleName) return '-';
    return roleName.split(/\s*-\s*/)[0]?.trim() || roleName;
  });
  readonly initialized = signal(false);
  readonly notificationUnreadCount = this.notificationCenter.unreadCount;

  readonly actions = computed<TopAction[]>(() => {
    const unread = this.notificationUnreadCount();
    const badge = unread > 99 ? '99+' : unread || undefined;

    return [
      // {id: 'bot', src: ASSET('robot'), label: 'Agente virtual'},
      // {id: 'search', src: ASSET('search'), label: 'Buscar'},
      // {id: 'folder', src: ASSET('document'), label: 'Documentos'},
      {id: 'firma-digital', src: ASSET('ic_signature'), label: 'Firma digital'},
      {id: 'bell', src: ASSET('notification'), label: 'Notificaciones', badge}
    ];
  });
  readonly activeAction = signal('');
  readonly openedSectionRoute = signal<string | null>(null);
  // Modo "ventas": usar menú inferior en móviles
  readonly isSalesMenu = signal(false);
  readonly isMobile = signal(false);
  salesMenu = [
    { label: 'Prospectos', route: '/crm/sales-advisors/sales-advisors-prospects',    icon: '/assets/icons/menu/ic_menu_contactbook.svg' },
    { label: 'Clientes',   route: '/crm/sales-advisors/sales-advisors-clients',      icon: '/assets/icons/menu/ic_menu_clients.svg' },
    { label: 'Cotizador',  route: '/crm/sales-advisors/sales-advisors-quoting',      icon: '/assets/icons/menu/ic_menu_table.svg' },
    { label: 'Proyectos',  route: '/crm/sales-advisors/sales-advisors-developments', icon: '/assets/icons/menu/ic_menu_building.svg' },
    { label: 'Cuenta',     route: '/crm/prospects/account',                           icon: '/assets/icons/menu/ic_menu_person.svg' }
  ];
  readonly currentUrl = signal('');
  readonly isNotificationsRoute = computed(() => this.isNotificationsPath(this.currentUrl() || this.router.url));
  // Menú hamburguesa móvil
  readonly mobileMenuOpen = signal(false);
  readonly mobileMenuItems = signal<MobileMenuItem[]>([]);
  readonly sidebarMenuItems = signal<MobileMenuItem[]>([]);
  readonly showRankingAction = computed(() => {
    const role = (this.userProfile()?.rolePrefix || '').toLowerCase().trim();
    if (role !== 'admin') return false;
    const normalized = (this.currentUrl() || '').split('?')[0].split('#')[0];
    const segments = normalized.split('/').filter(Boolean);
    if (segments.length === 0) return false;
    return segments.includes('prospects') && segments[segments.length - 1] === 'tracking';
  });

  /** Estado del logout para mostrar loading */
  readonly isLoggingOut = signal(false);

  /** Estado de carga del menú */
  readonly isMenuLoading = signal(false);
  readonly menuError = signal<string | null>(null);

  /* ------------------------------------------------------------------ */
  /*  Inyección de dependencias                                         */
  /* ------------------------------------------------------------------ */

  private destroy$ = new Subject<void>();
  private forceDefaultProfileNavigation = false;

  constructor(
    public menu: MenuService,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
    private userProfileService: UserProfileService,
    private menuApiService: MenuApiService
  ) {
  }

  /* ------------------------------------------------------------------ */
  /*  Lifecycle hooks                                                   */

  /* ------------------------------------------------------------------ */

  ngOnDestroy() {
    this.notificationCenter.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ------------------------------------------------------------------ */
  /*  Lógica de menús (tus métodos originales, sin cambios sustanciales) */

  /* ------------------------------------------------------------------ */

  ngOnInit() {
    // Detect viewport
    this.updateIsMobile();
    this.currentUrl.set(this.router.url);
    this.syncActiveTopActionWithRoute();
    this.notificationCenter.start();

    this.userProfileService.current$
      .pipe(
        filter((profile): profile is UserProfile => !!profile),
        takeUntil(this.destroy$)
      )
      .subscribe(profile => {
        const forceDefaultNavigation = this.forceDefaultProfileNavigation;
        this.forceDefaultProfileNavigation = false;
        this.applyProfileContext(profile, forceDefaultNavigation);
      });

    this.userProfileService.loadProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentUrl.set(this.router.url);
      this.syncMenuStateWithRoute();
      this.syncActiveTopActionWithRoute();
    });
  }

  @HostListener('window:resize')
  onResize() {
    const prev = this.isMobile();
    this.updateIsMobile();
    // Si cambió a desktop y es ventas, aseguramos cargar menú superior si no está
    if (prev && !this.isMobile() && this.isSalesMenu() && !this.menu.menuData) {
      this.loadUserMenu();
    }
    if (!prev && this.isMobile()) {
      this.isSalesMenu.set(true);
      this.loadMobileMenu();
    }
  }

  private updateIsMobile() {
    try {
      this.isMobile.set(window.innerWidth <= 1024);
    } catch {
      this.isMobile.set(false);
    }
  }

  goToRanking(): void {
    this.router.navigate(['consultants', 'ranking'], {relativeTo: this.route});
  }

  // Título activo para el menú inferior de ventas
  activeSalesLabel(): string {
    const url = this.currentUrl() || this.router.url;
    const items = this.mobileMenuItems();
    const item = items.find(i => url.startsWith(i.route));
    return item?.name ?? (items[0]?.name || 'Menú');
  }

  private loadUserMenu(redirectToDefault = false): void {
    this.isMenuLoading.set(true);
    this.menuError.set(null);

    this.menu.loadUserMenu()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
      next: () => {
        this.isMenuLoading.set(false);
        this.syncMenuStateWithRoute();
        if (redirectToDefault) {
          this.navigateToDefaultMenu();
        }
      },
      error: (error) => {
        this.isMenuLoading.set(false);
        this.menuError.set('Error al cargar el menú');
        console.error('Menu loading error:', error);
      }
      });
  }

  private loadMobileMenu(): void {
    this.menuApiService.getMobileMenu()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const mobileMenus = this.withFallbackMobileIcons(response.mobileMenus);
          const sidebarMenus = this.withFallbackMobileIcons(response.sidebarMenus);
          this.mobileMenuItems.set(mobileMenus);
          this.sidebarMenuItems.set(sidebarMenus);
          this.menu.setMobileMenuItems([
            ...mobileMenus,
            ...sidebarMenus
          ]);
        },
        error: (error) => {
          this.mobileMenuItems.set([]);
          this.sidebarMenuItems.set([]);
          this.menu.setMobileMenuItems([]);
          console.error('Error loading mobile menu:', error);
        }
      });
  }

  private withFallbackMobileIcons(items: MobileMenuItem[] | null | undefined): MobileMenuItem[] {
    const fallback = this.fallbackMobileMenuItems();
    if (!items?.length) {
      return [];
    }

    return items.map(item => {
      const fallbackItem = fallback.find(option =>
        option.route === item.route
        || option.name.toLowerCase() === item.name.toLowerCase()
      );

      return {
        ...item,
        icon: item.icon?.url ? item.icon : fallbackItem?.icon ?? null
      };
    });
  }

  private fallbackMobileMenuItems(): MobileMenuItem[] {
    return this.salesMenu.map(item => ({
      name: item.label,
      route: item.route,
      icon: {url: item.icon},
      type: 'mobile',
      permissionKey: null,
      actions: {}
    }));
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen.update(value => !value);
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
  }

  onMobileMenuItemClick(item: MobileMenuItem): void {
    this.router.navigateByUrl(item.route);
    this.closeMobileMenu();
  }

  onMainChange(main: MainOption) {
    this.activeMain.set(main);

    const subOptions = this.menu.getSubOptions(main);
    this.subOptions.set(subOptions);

    const visibleSubs = this.getVisibleSubOptions(subOptions);
    this.visibleSubOptions.set(visibleSubs);

    const firstSub = visibleSubs[0] ?? subOptions[0];
    this.activeSub.set(firstSub);

    const sections = firstSub ? this.getVisibleSections(this.menu.getSections(main, firstSub)) : [];
    this.sections.set(sections);

    const firstSection = sections[0];
    this.activeSection.set(firstSection);

    const firstVisibleSubSections = this.shouldShowSectionDropdown(firstSection)
      ? this.getVisibleSubSections(firstSection)
      : [];
    if (firstVisibleSubSections.length) {
      const firstSubSection = firstVisibleSubSections[0];
      this.selectedSubSection.set(firstSubSection);
      this.selectedSubSectionParentRoute.set(firstSection.route);
    } else {
      this.selectedSubSection.set(undefined);
      this.selectedSubSectionParentRoute.set(undefined);
    }

    if (!firstSub) {
      return;
    }

    if (firstSection) {
      const firstSubSection = this.shouldShowSectionDropdown(firstSection)
        ? this.getVisibleSubSections(firstSection)[0]
        : undefined;

      if (firstSubSection) {
        this.router.navigate(this.buildRouteSegments(
          main.route,
          firstSub.route,
          firstSection.route,
          firstSubSection.route
        ));
        return;
      }

      this.router.navigate(this.buildRouteSegments(
        main.route,
        firstSub.route,
        firstSection.route
      ));
      return;
    }

    this.router.navigate(this.buildRouteSegments(main.route, firstSub.route));
  }

  onSubChange(sub: SubOption) {
    this.activeSub.set(sub);
    const main = this.activeMain();
    if (!main) {
      return;
    }

    const sections = this.getVisibleSections(this.menu.getSections(main, sub));
    this.sections.set(sections);

    const firstSection = sections[0];
    this.activeSection.set(firstSection);

    if (firstSection) {
      const firstSubSection = this.shouldShowSectionDropdown(firstSection)
        ? this.getVisibleSubSections(firstSection)[0]
        : undefined;
      if (firstSubSection) {
        this.selectedSubSection.set(firstSubSection);
        this.selectedSubSectionParentRoute.set(firstSection.route);
        this.router.navigate(this.buildRouteSegments(
          main.route,
          sub.route,
          firstSection.route,
          firstSubSection.route
        ));
      } else {
        this.selectedSubSection.set(undefined);
        this.selectedSubSectionParentRoute.set(undefined);
        this.router.navigate(this.buildRouteSegments(
          main.route,
          sub.route,
          firstSection.route
        ));
      }
    } else {
      this.selectedSubSection.set(undefined);
      this.selectedSubSectionParentRoute.set(undefined);
      this.router.navigate(this.buildRouteSegments(
        main.route,
        sub.route
      ));
    }
  }

  onSectionClick(sec: Section, event?: MouseEvent) {
    event?.stopPropagation();

    // Toggle: if already open, close; if not, open
    if (this.openedSectionRoute() === sec.route) {
      this.openedSectionRoute.set(null);
      return;
    }

    // Only open if it has subSections
    const visibleSubSections = this.shouldShowSectionDropdown(sec)
      ? this.getVisibleSubSections(sec)
      : [];
    if (visibleSubSections.length) {
      this.activeSection.set(sec);
      this.openedSectionRoute.set(sec.route);
      return;
    }

    // No subSections: navigate directly
    this.openedSectionRoute.set(null);
    this.selectedSubSection.set(undefined);
    this.selectedSubSectionParentRoute.set(undefined);
    this.activeSection.set(sec);

    const main = this.activeMain();
    const sub = this.activeSub();
    if (!main || !sub) {
      return;
    }

    this.router.navigate(this.buildRouteSegments(
      main.route,
      sub.route,
      sec.route
    ));
  }

  onSubSectionClick(parent: Section, ss: SubSection, evt: MouseEvent) {
    evt.stopPropagation();
    this.selectedSubSection.set(ss);
    this.selectedSubSectionParentRoute.set(parent.route);
    this.activeSection.set(parent);
    const main = this.activeMain();
    const sub = this.activeSub();
    if (!main || !sub) {
      return;
    }
    this.router.navigate(this.buildRouteSegments(
      main.route,
      sub.route,
      parent.route,
      ss.route
    ));
    this.openedSectionRoute.set(null);
  }

  onAction(act: TopAction): void {
    this.activeAction.set(act.id);
    switch (act.id) {
      // case 'bot':
      //   this.router.navigateByUrl('/crm/bot');
      //   break;
      // case 'search':
      //   this.router.navigateByUrl('/crm/search');
      //   break;
      // case 'folder':
      //   this.router.navigateByUrl('/crm/folder');
      //   break;
      case 'firma-digital':
        this.router.navigateByUrl('/firma-digital');
        break;
      case 'bell':
        this.router.navigateByUrl('/notificaciones');
        break;
    }
  }


  toggleUserMenu(event?: MouseEvent): void {
    event?.stopPropagation();

    this.showUserMenu.update(value => {
      const next = !value;
      if (!next) {
        this.showProfileSwitchMenu.set(false);
      }
      return next;
    });
  }

  closeUserMenus(): void {
    this.showUserMenu.set(false);
    this.showProfileSwitchMenu.set(false);
  }

  onUserAction(extraRoute: string, event?: MouseEvent): void {
    event?.stopPropagation();

    if (extraRoute === 'switch-profile') {
      this.showProfileSwitchMenu.set(true);
      this.loadProfilesForSwitch();
      return;
    }

    if (extraRoute === 'logout') {
      this.executeLogout();
      return;
    }
  }

  onProfileSwitch(profile: MeProfile, event?: MouseEvent): void {
    event?.stopPropagation();

    if (profile.active || !profile.enable || this.switchingProfileUuid()) {
      return;
    }

    this.switchingProfileUuid.set(profile.uuid);

    this.auth.switchProfile(profile.uuid, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.forceDefaultProfileNavigation = true;
          this.userProfileService.loadProfile()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.loadProfilesForSwitch();
                this.closeUserMenus();
                this.switchingProfileUuid.set(null);
              },
              error: () => {
                this.forceDefaultProfileNavigation = false;
                this.switchingProfileUuid.set(null);
              }
            });
        },
        error: () => {
          this.switchingProfileUuid.set(null);
        }
      });
  }

  /**
   * 🚪 Ejecutar logout sin confirmación
   */
  private executeLogout(): void {
    this.isLoggingOut.set(true);
    this.closeUserMenus();
    this.notificationCenter.disconnect();

    // El AuthService ahora maneja todo el proceso de logout seguro
    this.auth.logout();

    // Nota: No necesitamos manejar el estado isLoggingOut aquí
    // porque el logout redirige inmediatamente al login
  }

  private loadProfilesForSwitch(): void {
    this.isLoadingProfiles.set(true);

    this.auth.getMyProfiles()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (profiles) => {
          this.myProfiles.set(profiles);
          this.isLoadingProfiles.set(false);
        },
        error: () => {
          this.isLoadingProfiles.set(false);
        }
      });
  }

  private applyProfileContext(profile: UserProfile, forceDefaultNavigation = false): void {
    this.userProfile.set(profile);
    this.auth.setRole(profile.rolePrefix);
    this.auth.setTypeUser$(profile.typeAdvisorPrefix);

    this.isSalesMenu.set(false);
    this.mobileMenuOpen.set(false);

    const serviceUserTypes = ['service', 'service-supervisor', 'service-manager'];
    const salesUserTypes   = ['ventas', 'sales', 'sales-advisor'];
    const managerUserTypes = ['manager', 'gerente', 'sales-manager', 'manager-sales'];
    const userType = (profile.typeAdvisorPrefix || '').toLowerCase().trim();
    const userRole = (profile.rolePrefix || '').toLowerCase().trim();
    const profileKeys = [userType, userRole].filter(Boolean);
    const isServiceProfile = profileKeys.some(key => serviceUserTypes.includes(key));
    const isManagerProfile = profileKeys.some(key => managerUserTypes.includes(key));
    const isSalesProfile = profileKeys.some(key => salesUserTypes.includes(key));

    if (this.isMobile()) {
      this.isSalesMenu.set(true);
      this.loadMobileMenu();
    }

    const currentUrl = this.router.url || '';
    const normalizedUrl = currentUrl.split('?')[0];
    const segments = normalizedUrl.split('/').filter(Boolean);
    const hasDeepLink = !forceDefaultNavigation && segments.length > 2;
    const isOnDefaultRoute = normalizedUrl === MAIN_ROUTE;
    const isNotificationsRoute = this.isNotificationsPath(normalizedUrl);
    const isAccountRoute = this.isAccountPath(normalizedUrl);
    const isDigitalSignatureRoute = this.isDigitalSignaturePath(normalizedUrl);
    const shouldNavigateToDefault = forceDefaultNavigation
      || (!hasDeepLink && !isNotificationsRoute && !isAccountRoute && !isDigitalSignatureRoute)
      || isOnDefaultRoute;

    if (userRole === 'legal-representative') {
      this.loadUserMenu();
      if (shouldNavigateToDefault && normalizedUrl !== '/firma-digital') {
        this.router.navigateByUrl('/firma-digital');
      }
      return;
    }

    // 0) Exclusivos de inmobiliaria -> Clientes inmobiliarios
    if (userType === EXCLUSIVE_REAL_ESTATE_TYPE) {
      this.loadUserMenu();
      const target = EXCLUSIVE_REAL_ESTATE_ROUTE;
      if (shouldNavigateToDefault && normalizedUrl !== target) {
        this.router.navigateByUrl(target);
      }
      return;
    }

    // 1) Servicio al cliente -> Service Requests
    if (isServiceProfile) {
      this.loadUserMenu(shouldNavigateToDefault);
      return;
    }

    // 2) Gerentes: menú superior (API)
    if (isManagerProfile) {
      this.loadUserMenu(shouldNavigateToDefault);
      return;
    }

    // 3) Ventas: menú inferior en móviles / menú superior en desktop
    if (isSalesProfile || !userType || userRole === 'admin') {
      this.isSalesMenu.set(true);
      this.loadUserMenu(shouldNavigateToDefault);
      return;
    }

    // 4) Otros usuarios: menú superior (API)
    this.loadUserMenu(shouldNavigateToDefault);
  }

  private safeNavigate(routeSegments: string[]) {
    const urlTree = this.router.createUrlTree(routeSegments);
    const exists = this.verifyRouteExist(this.router.config, routeSegments);

    if (exists) {
      this.router.navigateByUrl(urlTree);
    } else {
      const main = this.activeMain();
      const sub = this.activeSub();
      if (main && sub) {
        this.router.navigate(this.buildRouteSegments(main.route, sub.route, 'not-found'));
      } else {
        this.router.navigateByUrl(this.defaultMainRoute());
      }
    }
  }

  protected goMainRoute() {
    if (this.isMobile() && this.isSalesMenu()) {
      const target = this.firstMobileMenuRoute();
      if (target) {
        this.mobileMenuOpen.set(false);
        this.closeUserMenus();
        this.navigateByUrlIfChanged(target);
        return;
      }
    }

    this.navigateToDefaultMenu();
  }

  private defaultMainRoute(): string {
    const profile = this.userProfile();
    const userType = (profile?.typeAdvisorPrefix || '').toLowerCase().trim();

    if (userType === EXCLUSIVE_REAL_ESTATE_TYPE) {
      return EXCLUSIVE_REAL_ESTATE_ROUTE;
    }

    const menuDefaultRoute = this.menuDefaultRoute();
    if (menuDefaultRoute) {
      return menuDefaultRoute;
    }

    const firstAllowedRoute = this.firstAllowedMenuRoute(this.roleBlocksTrackingRoute());
    if (firstAllowedRoute) {
      return firstAllowedRoute;
    }

    if (this.roleBlocksTrackingRoute()) {
      const titlingFallbackRoute = this.titlingFallbackRoute();
      if (titlingFallbackRoute) {
        return titlingFallbackRoute;
      }
    }

    return MAIN_ROUTE;
  }

  private navigateToDefaultMenu(): void {
    const target = this.defaultMainRoute();
    this.navigateByUrlIfChanged(target);
  }

  private navigateByUrlIfChanged(target: string): void {
    const currentPath = this.normalizePath(this.router.url);
    const targetPath = this.normalizePath(target);

    if (targetPath === currentPath) {
      return;
    }

    this.router.navigateByUrl(target);
  }

  private firstMobileMenuRoute(): string | null {
    const route = this.mobileMenuItems()[0]?.route?.trim();
    if (!route) {
      return null;
    }

    return route.startsWith('/') ? route : `/${route}`;
  }

  private menuDefaultRoute(): string | null {
    if (!this.menu.menuData) {
      return null;
    }

    const defaults = this.menu.defaultRoutes;
    const main = this.menu.findMain(defaults.main) ?? this.menu.mainOptions[0];
    if (!main) {
      return null;
    }

    const subOptions = this.menu.getSubOptions(main);
    const visibleSubOptions = this.getVisibleSubOptions(subOptions);
    const sub = this.menu.findSub(main, defaults.sub) ?? visibleSubOptions[0] ?? subOptions[0];
    if (!sub) {
      return null;
    }

    const sections = this.getVisibleSections(this.menu.getSections(main, sub));
    const section = this.menu.findSection(main, sub, defaults.section) ?? sections[0];

    if (!section) {
      const routeSegments = this.buildRouteSegments(main.route, sub.route);
      if (routeSegments.length && this.verifyRouteExist(this.router.config, routeSegments)) {
        return `/${routeSegments.join('/')}`;
      }
      return null;
    }

    const routeSegments = this.buildRouteSegments(main.route, sub.route, section.route);
    if (routeSegments.length && this.verifyRouteExist(this.router.config, routeSegments)) {
      return `/${routeSegments.join('/')}`;
    }

    const visibleSubSections = this.getVisibleSubSections(section);
    const firstSubSection = visibleSubSections[0];
    if (!firstSubSection) {
      return null;
    }

    const nestedRouteSegments = this.buildRouteSegments(main.route, sub.route, section.route, firstSubSection.route);
    if (nestedRouteSegments.length && this.verifyRouteExist(this.router.config, nestedRouteSegments)) {
      return `/${nestedRouteSegments.join('/')}`;
    }

    return null;
  }

  private verifyRouteExist(routeConfigs: any[], segments: string[]): boolean {
    if (!segments.length) {
      return true;
    }

    for (const route of routeConfigs ?? []) {
      const rawPath = String(route?.path ?? '').trim();
      if (!rawPath || rawPath === '**') {
        continue;
      }

      const routeSegments = rawPath.split('/').filter(Boolean);
      if (!routeSegments.length || routeSegments.length > segments.length) {
        continue;
      }

      const matches = routeSegments.every((routeSegment: string, index: number) => {
        const currentSegment = segments[index];
        return routeSegment.startsWith(':') || routeSegment === currentSegment;
      });

      if (!matches) {
        continue;
      }

      const remainingSegments = segments.slice(routeSegments.length);
      if (!remainingSegments.length) {
        if (route.redirectTo || route.loadComponent || route.component || route.children) {
          return true;
        }
      }

      if (route.children && this.verifyRouteExist(route.children, remainingSegments)) {
        return true;
      }
    }

    return false;
  }

  private buildRouteSegments(...segments: Array<string | null | undefined>): string[] {
    return segments
      .filter((seg): seg is string => typeof seg === 'string' && seg.length > 0)
      .flatMap(seg => seg.split('/').filter(Boolean));
  }

  /**
   * Determines if the given section is the currently active section or parent of the active sub-section.
   * This is used for setting the 'active' class on the navigation button.
   */
  isSectionActive(sec: Section): boolean {
    // Section is active if:
    // - It's the current active section
    // - It's the parent of the currently selected sub-section
    // - Its dropdown is currently open
    return (
      this.activeSection()?.route === sec.route ||
      this.selectedSubSectionParentRoute() === sec.route ||
      this.openedSectionRoute() === sec.route
    );
  }

  private getVisibleSubOptions(subOptions: SubOption[]): SubOption[] {
    return subOptions.filter(opt => opt.showLevel !== false);
  }

  getVisibleSubSections(section: Section | undefined): SubSection[] {
    return (section?.subSections ?? []).filter(subSection => subSection.actions?.['list'] !== false);
  }

  shouldShowSectionDropdown(section: Section | undefined): boolean {
    if (!section) {
      return false;
    }

    if (!this.getVisibleSubSections(section).length) {
      return false;
    }

    if (this.isDetailOnlySection(section) && !this.isDetailSectionContext(section)) {
      return false;
    }

    return true;
  }

  private getVisibleSections(sections: Section[]): Section[] {
    return sections.filter(section => section.actions?.['list'] !== false || this.getVisibleSubSections(section).length > 0);
  }

  private isDetailOnlySection(section: Section): boolean {
    const activeMain = this.activeMain()?.route;
    const activeSub = this.activeSub()?.route;

    return (
      section.permissionKey === 'crm.prospects.prospects-tracking'
      || (activeMain === 'crm' && activeSub === 'prospects' && section.route === 'tracking')
    );
  }

  private isDetailSectionContext(section: Section): boolean {
    if (!this.isDetailOnlySection(section)) {
      return true;
    }

    const normalizedUrl = (this.currentUrl() || this.router.url || '').split('?')[0].split('#')[0];
    return /\/prospect\/[^/]+(?:\/|$)/.test(normalizedUrl);
  }

  private syncMenuStateWithRoute(): void {
    if (!this.menu.menuData) return; // Esperar a que se cargue el menú

    const resolved = this.menu.resolveRouteContext(this.router.url);
    const defaults = this.menu.defaultRoutes;

    const main = resolved?.main ?? this.menu.findMain(defaults.main);
    if (!main) {
      console.warn('No se encontró el main option para la ruta:', this.router.url || defaults.main);
      return;
    }
    this.activeMain.set(main);

    const subOptions = this.menu.getSubOptions(main);
    this.subOptions.set(subOptions);
    const visibleSubs = this.getVisibleSubOptions(subOptions);
    this.visibleSubOptions.set(visibleSubs);
    this.initialized.set(true);

    const sub = resolved?.sub ?? visibleSubs[0] ?? subOptions[0];
    this.activeSub.set(sub);

    const sections = sub ? this.getVisibleSections(this.menu.getSections(main, sub)) : [];
    this.sections.set(sections);

    if (this.isProspectsTrackingRoute(this.router.url) && (!this.hasProspectsTrackingAccess() || this.roleBlocksTrackingRoute())) {
      const fallbackRoute = this.firstAllowedMenuRoute(this.roleBlocksTrackingRoute()) ?? this.titlingFallbackRoute();
      const currentPath = this.normalizePath(this.router.url);
      if (fallbackRoute && fallbackRoute !== currentPath) {
        this.router.navigateByUrl(fallbackRoute);
        return;
      }
    }

    const section = resolved?.section
      ? sections.find(sec => sec.route === resolved.section?.route)
      : sections[0];
    const subSection = resolved?.subSection && section && this.shouldShowSectionDropdown(section)
      ? this.getVisibleSubSections(section).find(ss => ss.route === resolved.subSection?.route)
      : undefined;

    this.activeSection.set(section ?? sections[0]);
    this.selectedSubSection.set(subSection);
    this.selectedSubSectionParentRoute.set(subSection ? section?.route : undefined);
    this.openedSectionRoute.set(null);
  }

  private firstAllowedMenuRoute(excludeTrackingRoute = false): string | null {
    const menuData = this.menu.menuData;
    if (!menuData) {
      return null;
    }

    for (const main of this.menu.mainOptions) {
      const allSubOptions = this.menu.getSubOptions(main);
      const visibleSubOptions = this.getVisibleSubOptions(allSubOptions);
      const subCandidates = visibleSubOptions;

      if (!subCandidates.length) {
        continue;
      }

      for (const sub of subCandidates) {
        const sections = this.getVisibleSections(this.menu.getSections(main, sub));
        const firstSection = sections[0];

        if (firstSection) {
          const visibleSubSections = this.getVisibleSubSections(firstSection);
          const routeSegments = visibleSubSections.length
            ? this.buildRouteSegments(main.route, sub.route, firstSection.route, visibleSubSections[0].route)
            : this.buildRouteSegments(main.route, sub.route, firstSection.route);

          if (routeSegments.length && this.verifyRouteExist(this.router.config, routeSegments)) {
            const candidate = `/${routeSegments.join('/')}`;
            if (!excludeTrackingRoute || !this.isProspectsTrackingRoute(candidate)) {
              return candidate;
            }
          }
        } else {
          const routeSegments = this.buildRouteSegments(main.route, sub.route);
          if (routeSegments.length && this.verifyRouteExist(this.router.config, routeSegments)) {
            const candidate = `/${routeSegments.join('/')}`;
            if (!excludeTrackingRoute || !this.isProspectsTrackingRoute(candidate)) {
              return candidate;
            }
          }
        }
      }
    }

    return null;
  }

  private hasProspectsTrackingAccess(): boolean {
    const main = this.menu.findMain('crm');
    if (!main) {
      return false;
    }

    const sub = this.menu.findSub(main, 'prospects');
    if (!sub) {
      return false;
    }

    const trackingSection = this.menu
      .getSections(main, sub)
      .find(section => this.isTrackingSectionRoute(section.route));

    if (!trackingSection) {
      return false;
    }

    return trackingSection.actions?.['list'] !== false;
  }

  private isProspectsTrackingRoute(url: string): boolean {
    return this.normalizePath(url) === '/crm/prospects/tracking';
  }

  private syncActiveTopActionWithRoute(): void {
    if (this.isNotificationsPath(this.router.url)) {
      this.activeAction.set('bell');
      return;
    }

    if (this.isDigitalSignaturePath(this.router.url)) {
      this.activeAction.set('firma-digital');
      return;
    }

    if (this.activeAction() === 'bell' || this.activeAction() === 'firma-digital') {
      this.activeAction.set('');
    }
  }

  private normalizePath(url: string): string {
    const path = (url || '').split('?')[0].split('#')[0];
    return path || '/';
  }

  private isNotificationsPath(url: string): boolean {
    const path = this.normalizePath(url);
    return path === '/notificaciones' || path.startsWith('/notificaciones/');
  }

  private isDigitalSignaturePath(url: string): boolean {
    const path = this.normalizePath(url);
    return path === '/firma-digital' || path.startsWith('/firma-digital/');
  }

  private isAccountPath(url: string): boolean {
    const path = this.normalizePath(url);
    return path === '/crm/account' || path === '/crm/prospects/account';
  }

  private isTrackingSectionRoute(route: string | null | undefined): boolean {
    const normalized = String(route ?? '').trim().toLowerCase();
    return (
      normalized === 'tracking'
      || normalized === 'prospects-tracking'
      || normalized.endsWith('-tracking')
    );
  }

  private roleBlocksTrackingRoute(): boolean {
    const role = this.normalizeRoleKey(this.userProfile()?.rolePrefix);
    const advisorType = this.normalizeRoleKey(this.userProfile()?.typeAdvisorPrefix);
    return role === 'titulacion' || advisorType === 'titulacion';
  }

  private titlingFallbackRoute(): string | null {
    const candidates = [
      '/crm/control-panel/dashboard-records',
      '/crm/record/sales-records',
      '/crm/titling/deeds'
    ];

    for (const candidate of candidates) {
      const segments = this.buildRouteSegments(candidate);
      if (segments.length && this.verifyRouteExist(this.router.config, segments)) {
        return candidate;
      }
    }

    return null;
  }

  private normalizeRoleKey(value: string | null | undefined): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

}
